/**
 * The HTTP client. One instance per tab, over one pooled HTTP/2 connection (D8).
 *
 * What it owns, and why each one is here rather than at a call site:
 *
 * - **`ETag` / `304`.** Parsed from the response, so the client honours exactly
 *   what the origin sets (`cache.ts`).
 * - **`problem+json` → typed errors.** Every non-2xx, including router 404s and
 *   panic-handler 500s (`problem.ts`).
 * - **`Idempotency-Key` on every POST.** Required by §6.1; a POST without one is
 *   `400 INVALID_PARAMETER`. It has no default at the protocol level *because a
 *   defaulted idempotency key is not an idempotency key* — but the client must
 *   still never send a POST without one, so one is generated here unless the
 *   caller supplies it. Order placement always supplies it: one logical order is
 *   one `client_order_id`, one `Idempotency-Key` and one `nonce_ms`, generated
 *   together and reused on every retry of that same order (§6.3).
 * - **`401` → `GET /auth/session` once, then fail.** Deliberately not a retry of
 *   the original request: `UNAUTHENTICATED` says to run SIWE, not to try again.
 * - **`server_time_ms` into the clock.** Present on every payload including
 *   errors, so the free drift correction of §9.6 costs nothing.
 * - **Idle tracking**, which `keep-warm.ts` reads.
 *
 * What it deliberately does **not** own: retries. `retryable` is the server's
 * opinion and it is authoritative (§4.4.2); a transport that second-guesses it
 * is a transport that places duplicate orders.
 */

import { API_BASE_URL, CLIENT_ID } from '@/lib/env'
import { sharedClock, type Clock } from '@/lib/clock'
import { ResponseCache, policyFrom } from './cache'
import { HttpError, NetworkError, parseProblem, retryAfterMsFrom } from './problem'

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>
export type Query = Record<string, QueryValue>

export interface RequestOptions {
  query?: Query | undefined
  signal?: AbortSignal | undefined
  headers?: Record<string, string> | undefined
  /**
   * Bypass a fresh cache entry and force revalidation. `CONFIG_STALE` tells the
   * client to re-fetch `/config` with `Cache-Control: no-cache` (§4.3).
   */
  noCache?: boolean | undefined
}

export interface MutationOptions extends RequestOptions {
  /**
   * Reuse the *same* key on every retry of one logical request. Omitted, a
   * fresh key is generated — correct for a first attempt, wrong for a retry,
   * which is why anything retryable passes its own.
   */
  idempotencyKey?: string | undefined
  /** `X-UD-Key` / `X-UD-Signature` / `X-UD-Nonce` for `POST /orders` (§3.4.2). */
  signature?: SessionKeySignature | undefined
}

export interface SessionKeySignature {
  key: string
  signature: string
  nonce: string
}

export interface HttpClientOptions {
  baseUrl?: string
  fetchImpl?: typeof fetch
  clock?: Clock
  monoNow?: () => number
  /** Injected so tests need no `crypto`, and so an order can pass its own key. */
  newIdempotencyKey?: () => string
  /**
   * Called once when a `401` arrives, to re-read `GET /auth/session`. Returning
   * a promise serialises concurrent 401s onto one recheck.
   */
  onUnauthenticated?: () => Promise<void>
  clientId?: string
}

export const IDLE_KEEP_WARM_MS = 45_000

/** One rate-limit bucket, as the last response described it (RFC 9331). */
export interface RateAllowance {
  /** `RateLimit-Policy` — the bucket that governed that response. */
  policy: string
  /** `RateLimit-Remaining` at that moment. */
  remaining: number
  /** `RateLimit-Reset`, resolved against the corrected clock. */
  resetAtMs: number | null
}

export class HttpClient {
  readonly #baseUrl: string
  readonly #fetch: typeof fetch
  readonly #clock: Clock
  readonly #monoNow: () => number
  readonly #newKey: () => string
  readonly #clientId: string
  readonly #cache: ResponseCache
  /** `RateLimit-Policy` → what the last response on that bucket reported. */
  readonly #allowances = new Map<string, RateAllowance>()

  #onUnauthenticated: (() => Promise<void>) | undefined
  #authRecheck: Promise<void> | null = null
  #lastActivityMonoMs: number
  /** In-flight GETs, keyed like the cache: two panels asking for one snapshot
   *  at the same instant is a cold-load shape, not an edge case. */
  readonly #inFlight = new Map<string, SharedRequest<unknown>>()

  constructor(options: HttpClientOptions = {}) {
    this.#baseUrl = (options.baseUrl ?? API_BASE_URL).replace(/\/+$/, '')
    this.#fetch = options.fetchImpl ?? ((...args) => globalThis.fetch(...args))
    this.#clock = options.clock ?? sharedClock()
    this.#monoNow = options.monoNow ?? (() => performance.now())
    this.#newKey = options.newIdempotencyKey ?? defaultIdempotencyKey
    this.#clientId = options.clientId ?? CLIENT_ID
    this.#onUnauthenticated = options.onUnauthenticated
    this.#cache = new ResponseCache({ monoNow: this.#monoNow })
    this.#lastActivityMonoMs = this.#monoNow()
  }

  get cache(): ResponseCache {
    return this.#cache
  }

  /** Milliseconds since the last request completed. `keep-warm.ts` reads this. */
  get idleMs(): number {
    return this.#monoNow() - this.#lastActivityMonoMs
  }

  /** Wired after construction, because the session endpoint is itself an
   *  endpoint module that needs the client. */
  setUnauthenticatedHandler(handler: () => Promise<void>): void {
    this.#onUnauthenticated = handler
  }

  get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.#cachedGet<T>(path, options)
  }

  post<T>(path: string, body?: unknown, options: MutationOptions = {}): Promise<T> {
    return this.#send<T>('POST', path, body, options)
  }

  delete<T>(path: string, options: MutationOptions = {}): Promise<T> {
    return this.#send<T>('DELETE', path, undefined, options)
  }

  /** `HEAD /time`, ~90 bytes, so the next order does not pay a TLS handshake. */
  async head(path: string, options: RequestOptions = {}): Promise<void> {
    await this.#send<void>('HEAD', path, undefined, options)
  }

  // -------------------------------------------------------------------------

  /**
   * A GET, coalesced with any identical GET already in flight.
   *
   * **The shared request does not run on any one caller's signal**, and that is
   * the whole subtlety. An earlier version handed every caller the same promise
   * — including the first caller's `AbortSignal` — so when the first caller
   * unmounted and aborted, every other caller's request died with
   * `signal is aborted without reason`, having never aborted anything. React 19
   * `StrictMode` mounts each effect twice, so in development that was *every*
   * coalesced request, and it stayed invisible from Phase 1 until Phase 4 became
   * the first code with two callers of one URL (register §F20).
   *
   * So: the fetch runs on an internal controller, each caller races the shared
   * promise against their own signal, and the internal controller is aborted
   * only once every caller has gone away — which keeps the point of coalescing
   * (one request) without letting one caller cancel another's.
   */
  async #cachedGet<T>(path: string, options: RequestOptions): Promise<T> {
    const url = this.#url(path, options.query)
    const key = `GET ${url}`

    const entry = this.#cache.get<T>(key)
    if (entry && !options.noCache && this.#cache.isFresh(entry)) {
      return entry.body
    }

    let shared = this.#inFlight.get(key) as SharedRequest<T> | undefined
    if (!shared) {
      // No caller signal reaches the shared request — that is the entire point.
      const rest = { ...options }
      delete rest.signal
      const record: SharedRequest<T> = {
        promise: this.#send<T>('GET', path, undefined, rest, key).finally(() => {
          // Only if it is still ours: a later request for the same URL may have
          // replaced this record already.
          if (this.#inFlight.get(key) === record) this.#inFlight.delete(key)
        }),
      }
      shared = record
      this.#inFlight.set(key, record)
    }

    return attachToShared(shared, options.signal, 'GET', path)
  }

  async #send<T>(
    method: string,
    path: string,
    body: unknown,
    options: MutationOptions,
    cacheKey?: string,
  ): Promise<T> {
    const url = this.#url(path, options.query)
    const headers = new Headers(options.headers)
    headers.set('Accept', 'application/json, application/problem+json')
    headers.set('X-UD-Client', this.#clientId)

    if (body !== undefined) headers.set('Content-Type', 'application/json')

    if (method === 'POST') {
      // §6.1: required on every POST, no default at the protocol level.
      headers.set('Idempotency-Key', options.idempotencyKey ?? this.#newKey())
    }
    if (options.signature) {
      headers.set('X-UD-Key', options.signature.key)
      headers.set('X-UD-Signature', options.signature.signature)
      headers.set('X-UD-Nonce', options.signature.nonce)
    }

    const cached = cacheKey ? this.#cache.get(cacheKey) : undefined
    if (cached?.etag && !options.noCache) headers.set('If-None-Match', cached.etag)
    if (options.noCache) headers.set('Cache-Control', 'no-cache')

    let response: Response
    try {
      response = await this.#fetch(url, {
        method,
        headers,
        // The session is an httpOnly cookie (D3). Never a bearer token in storage.
        credentials: 'include',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...(options.signal ? { signal: options.signal } : {}),
      })
    } catch (cause) {
      const aborted = options.signal?.aborted === true
      throw new NetworkError(method, path, cause, aborted)
    } finally {
      this.#lastActivityMonoMs = this.#monoNow()
    }

    // Free clock correction, from the header so it survives an empty body (§2.9).
    const headerTime = Number(response.headers.get('X-UD-Server-Time-Ms'))
    if (Number.isFinite(headerTime) && headerTime > 0) this.#clock.observeServerTime(headerTime)

    this.#noteAllowance(response)

    if (response.status === 304 && cacheKey && cached) {
      this.#cache.refresh(cacheKey, cached.serverTimeMs)
      return cached.body as T
    }

    if (response.status === 204 || method === 'HEAD') {
      if (!response.ok) throw await this.#toError(method, path, response)
      return undefined as T
    }

    const payload = await readJson(response)

    if (!response.ok) {
      const error = await this.#toError(method, path, response, payload)
      if (error.isAuthFailure) await this.#recheckSession()
      throw error
    }

    const serverTimeMs = readServerTime(payload)
    if (serverTimeMs !== null) this.#clock.observeServerTime(serverTimeMs)

    if (cacheKey) {
      const policy = policyFrom(response.headers.get('Cache-Control'))
      const etag = response.headers.get('ETag')
      if (policy.store || etag) {
        this.#cache.set(cacheKey, { body: payload, etag, policy, serverTimeMs })
      }
    }

    return payload as T
  }

  /**
   * §9.2.10. Remember what the last response said was left.
   *
   * > A rejected order in the last second of a 60-second market is a lost
   * > market, and a rejection the UI could have predicted is a UI bug.
   *
   * **Keyed by `RateLimit-Policy`, and dropped when there is no policy header**,
   * because the buckets are per scope: a `GET /leaderboard` reporting 1,199 of
   * 1,200 reads left says nothing about the twenty orders a second the trade
   * bucket allows, and disabling the Buy button on a *read* allowance would be
   * inventing a limit the venue never applied. RFC 9331 is what makes this
   * possible at all — without the policy name the numbers are unattributable.
   */
  #noteAllowance(response: Response): void {
    const policy = response.headers.get('RateLimit-Policy')
    const remaining = Number(response.headers.get('RateLimit-Remaining'))
    if (!policy || !Number.isFinite(remaining)) return
    const resetSeconds = Number(response.headers.get('RateLimit-Reset'))
    this.#allowances.set(policy, {
      policy,
      remaining,
      // The server's own seconds, resolved against the corrected clock so the
      // countdown in the button is in the same frame of reference as the one in
      // the strip (invariant 7).
      resetAtMs: Number.isFinite(resetSeconds) ? this.#clock.now() + resetSeconds * 1000 : null,
    })
  }

  /**
   * What the venue last said was left on one bucket, or `null` if it has never
   * said. `null` is not "you may not trade" — it is "there is nothing to
   * predict", and the ticket must treat it that way.
   */
  allowance(policy: string): RateAllowance | null {
    return this.#allowances.get(policy) ?? null
  }

  async #toError(method: string, path: string, response: Response, payload?: unknown): Promise<HttpError> {
    const body = payload === undefined ? await readJson(response) : payload
    const problem = parseProblem(body)
    return new HttpError({
      status: response.status,
      problem,
      requestId: response.headers.get('X-UD-Request-Id'),
      retryAfterMs: retryAfterMsFrom(response.headers.get('Retry-After'), problem),
      method,
      path,
      ...(problem === null && typeof body === 'string' ? { rawBody: body } : {}),
    })
  }

  /**
   * §3.2: `GET /auth/session` again after any `401`. **Once** — concurrent 401s
   * share one recheck, and the original request still fails. A transport that
   * silently replays the request after re-authenticating is a transport that
   * places an order the user did not confirm.
   */
  async #recheckSession(): Promise<void> {
    if (!this.#onUnauthenticated) return
    this.#authRecheck ??= this.#onUnauthenticated().finally(() => {
      this.#authRecheck = null
    })
    try {
      await this.#authRecheck
    } catch {
      // The recheck's own failure is the caller's problem to render, not ours
      // to mask the original 401 with.
    }
  }

  #url(path: string, query?: Query): string {
    const qs = encodeQuery(query)
    return `${this.#baseUrl}${path}${qs ? `?${qs}` : ''}`
  }
}

/**
 * `snake_case` on the wire, both directions — including query parameters
 * (§2.1). Repeated parameters are `explode: true` (`state=OPEN&state=FIXING`),
 * never comma-joined, because the spec rejects an unknown parameter shape
 * rather than ignoring it.
 */
export function encodeQuery(query: Query | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value as string | number | boolean))
    }
  }
  return params.toString()
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (text.length === 0) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

function readServerTime(payload: unknown): number | null {
  if (typeof payload !== 'object' || payload === null) return null
  const value = (payload as { server_time_ms?: unknown }).server_time_ms
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function defaultIdempotencyKey(): string {
  // 16–128 characters, client-generated (§6.1).
  const c: Crypto | undefined = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID()
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  throw new Error('no WebCrypto: cannot generate an Idempotency-Key, and a POST without one is a 400')
}

/** One in-flight GET, shared by every caller that asked for the same URL. */
interface SharedRequest<T> {
  promise: Promise<T>
}

/**
 * Give one caller its own view of a shared request.
 *
 * The caller sees exactly two outcomes: the shared result, or **its own** abort.
 * It never sees another caller's abort, and its own abort never takes the
 * request away from anyone still waiting.
 *
 * **The shared request itself is unsignalled and always runs to completion**,
 * which is deliberate and was arrived at the hard way. A first version aborted
 * it once the last caller had gone, which is tidier and has a race:
 * `StrictMode` unmounts and remounts an effect in the same tick, so the
 * departing caller dropped the count to zero and cancelled the fetch a
 * microtask before the arriving caller attached to the very same record. The
 * result was a request that reliably failed exactly once per mount, in
 * development only.
 *
 * There is nothing to reclaim by cancelling anyway: this path is GETs, the
 * response lands in the cache, and the next caller gets it for free.
 */
function attachToShared<T>(
  shared: SharedRequest<T>,
  signal: AbortSignal | undefined,
  method: string,
  path: string,
): Promise<T> {
  if (!signal) return shared.promise

  return new Promise<T>((resolve, reject) => {
    const abortError = () => new NetworkError(method, path, signal.reason, true)
    if (signal.aborted) {
      reject(abortError())
      return
    }
    const onAbort = () => reject(abortError())
    signal.addEventListener('abort', onAbort, { once: true })
    shared.promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort)
        resolve(value)
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort)
        reject(error)
      },
    )
  })
}

/** The tab's client. One instance, over one pooled connection (D8). */
let shared: HttpClient | null = null

export function sharedHttpClient(): HttpClient {
  shared ??= new HttpClient()
  return shared
}

export function setSharedHttpClient(client: HttpClient | null): void {
  shared = client
}
